import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, ChevronDown, FileText, Folder, FolderOpen, Search, X, Check } from 'lucide-react';
import './HierarchicalFileSelector.css';
import config, { getDefaultHeaders } from '../config/api';
import { useCountry } from '../context/CountryContext';

const HierarchicalFileSelector = ({ 
  isOpen, 
  onClose, 
  onFilesSelected, 
  selectedFiles = [], 
  apiBaseUrl = config.apiBaseUrl 
}) => {
  const { country } = useCountry();
  const [hierarchicalData, setHierarchicalData] = useState({});
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectionStats, setSelectionStats] = useState({
    totalFiles: 0,
    selectedFiles: 0
  });
  const [filteredData, setFilteredData] = useState({});
  const searchInputRef = useRef(null);

  // Load file mapping data
  useEffect(() => {
    if (isOpen) {
      loadFileMapping();
      // Focus search input when modal opens
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen]);

  // Filter data based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredData(hierarchicalData);
    } else {
      const filtered = filterHierarchicalData(hierarchicalData, searchQuery.toLowerCase());
      setFilteredData(filtered);
    }
  }, [hierarchicalData, searchQuery]);

  const loadFileMapping = async () => {
    try {
      setLoading(true);
      
      // Fetch from the API endpoint with country header
      const response = await fetch(`${apiBaseUrl}/api/available-files`, {
        headers: getDefaultHeaders(country)
      });
      if (!response.ok) {
        throw new Error('Failed to fetch file mapping');
      }
      
      const mappingData = await response.json();
      
      console.log('API Response:', mappingData);
      console.log('Files count:', mappingData.files?.length);
      
      // The API returns files in a flat array, we need to convert it to hierarchical structure
      const hierarchical = createHierarchicalStructureFromFiles(mappingData.files);
      console.log('Hierarchical structure:', hierarchical);
      
      setHierarchicalData(hierarchical);
      setFilteredData(hierarchical);
      
      // Count total files
      const totalFiles = mappingData.files.length;
      
      // Update selection stats
      setSelectionStats(prev => ({
        ...prev,
        totalFiles: totalFiles
      }));
      
    } catch (error) {
      console.error('Error loading file mapping:', error);
      setHierarchicalData({});
      setFilteredData({});
    } finally {
      setLoading(false);
    }
  };

  const createHierarchicalStructureFromFiles = (files) => {
    const structure = {};
    
    files.forEach(file => {
      const { bank, year, quarter, document_type, segment, filename, file_path, description, file_size } = file;
      
      // Initialize bank if not exists
      if (!structure[bank]) {
        structure[bank] = {
          type: 'bank',
          name: bank,
          path: bank,
          children: {},
          fileCount: 0,
          selected: false,
          partiallySelected: false
        };
      }
      
      // Initialize year if not exists
      if (!structure[bank].children[year]) {
        structure[bank].children[year] = {
          type: 'year',
          name: year,
          path: `${bank}/${year}`,
          children: {},
          fileCount: 0,
          selected: false,
          partiallySelected: false
        };
      }
      
      // Initialize quarter if not exists
      if (!structure[bank].children[year].children[quarter]) {
        structure[bank].children[year].children[quarter] = {
          type: 'quarter',
          name: quarter,
          path: `${bank}/${year}/${quarter}`,
          children: {},
          fileCount: 0,
          selected: false,
          partiallySelected: false
        };
      }
      
      // Initialize document type if not exists
      if (!structure[bank].children[year].children[quarter].children[document_type]) {
        structure[bank].children[year].children[quarter].children[document_type] = {
          type: 'docType',
          name: document_type,
          path: `${bank}/${year}/${quarter}/${document_type}`,
          children: {},
          fileCount: 0,
          selected: false,
          partiallySelected: false
        };
      }
      
      // Initialize segment if not exists (for presentation files)
      if (segment && !structure[bank].children[year].children[quarter].children[document_type].children[segment]) {
        structure[bank].children[year].children[quarter].children[document_type].children[segment] = {
          type: 'segment',
          name: segment,
          path: `${bank}/${year}/${quarter}/${document_type}/${segment}`,
          children: {},
          fileCount: 0,
          selected: false,
          partiallySelected: false
        };
      }
      
      // Add file to appropriate location
      const fileData = {
        relative_path: file_path,
        description: description,
        file_size: file_size,
        bank: bank,
        year: year,
        quarter: quarter,
        document_type: document_type
      };
      
      if (segment) {
        // Presentation file with segment
        structure[bank].children[year].children[quarter].children[document_type].children[segment].children[filename] = {
          type: 'file',
          name: filename,
          path: file_path,
          fileData: fileData,
          selected: selectedFiles.includes(file_path),
          partiallySelected: false
        };
        
        // Update file counts up the hierarchy
        structure[bank].children[year].children[quarter].children[document_type].children[segment].fileCount++;
      } else {
        // Transcript file (no segment)
        structure[bank].children[year].children[quarter].children[document_type].children[filename] = {
          type: 'file',
          name: filename,
          path: file_path,
          fileData: fileData,
          selected: selectedFiles.includes(file_path),
          partiallySelected: false
        };
      }
      
      // Update file counts up the hierarchy
      structure[bank].children[year].children[quarter].children[document_type].fileCount++;
      structure[bank].children[year].children[quarter].fileCount++;
      structure[bank].children[year].fileCount++;
      structure[bank].fileCount++;
    });
    
    return structure;
  };


  const filterHierarchicalData = (data, query) => {
    const filtered = {};
    
    Object.entries(data).forEach(([key, node]) => {
      if (node.type === 'file') {
        // Check if file matches search
        if (node.name.toLowerCase().includes(query) || 
            node.fileData?.description?.toLowerCase().includes(query)) {
          filtered[key] = { ...node };
        }
      } else if (node.children) {
        // Recursively filter children
        const filteredChildren = filterHierarchicalData(node.children, query);
        if (Object.keys(filteredChildren).length > 0) {
          filtered[key] = {
            ...node,
            children: filteredChildren
          };
        }
      }
    });
    
    return filtered;
  };

  const toggleNode = (path) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  const toggleSelection = (path, node) => {
    if (node.type === 'file') {
      // Toggle individual file selection
      const newSelectedFiles = selectedFiles.includes(path) 
        ? selectedFiles.filter(f => f !== path)
        : [...selectedFiles, path];
      
      onFilesSelected(newSelectedFiles);
    } else {
      // Toggle folder selection (select/deselect all files in this folder)
      const filesInFolder = getAllFilesInNode(node);
      const allSelected = filesInFolder.every(filePath => selectedFiles.includes(filePath));
      
      let newSelectedFiles;
      if (allSelected) {
        // Deselect all files in this folder
        newSelectedFiles = selectedFiles.filter(filePath => !filesInFolder.includes(filePath));
      } else {
        // Select all files in this folder
        const newFiles = filesInFolder.filter(filePath => !selectedFiles.includes(filePath));
        newSelectedFiles = [...selectedFiles, ...newFiles];
      }
      
      onFilesSelected(newSelectedFiles);
    }
  };

  const getAllFilesInNode = (node) => {
    const files = [];
    
    if (node.type === 'file') {
      files.push(node.path);
    } else if (node.children) {
      Object.values(node.children).forEach(child => {
        files.push(...getAllFilesInNode(child));
      });
    }
    
    return files;
  };

  const isNodeSelected = (node) => {
    if (node.type === 'file') {
      return selectedFiles.includes(node.path);
    } else {
      const filesInNode = getAllFilesInNode(node);
      return filesInNode.length > 0 && filesInNode.every(filePath => selectedFiles.includes(filePath));
    }
  };

  const isNodePartiallySelected = (node) => {
    if (node.type === 'file') {
      return false;
    } else {
      const filesInNode = getAllFilesInNode(node);
      const selectedInNode = filesInNode.filter(filePath => selectedFiles.includes(filePath));
      return selectedInNode.length > 0 && selectedInNode.length < filesInNode.length;
    }
  };

  const getSelectedCount = (node) => {
    const filesInNode = getAllFilesInNode(node);
    const selectedInNode = filesInNode.filter(filePath => selectedFiles.includes(filePath));
    return selectedInNode.length;
  };

  const renderNode = (node, level = 0) => {
    const isExpanded = expandedNodes.has(node.path);
    const isSelected = isNodeSelected(node);
    const isPartiallySelected = isNodePartiallySelected(node);
    const hasChildren = node.children && Object.keys(node.children).length > 0;
    const selectedCount = getSelectedCount(node);
    
    return (
      <div key={node.path} className="tree-node">
        <div 
          className={`tree-item level-${level} ${isSelected ? 'selected' : ''} ${isPartiallySelected ? 'partially-selected' : ''}`}
          style={{ paddingLeft: `${level * 24 + 12}px` }}
        >
          <div className="tree-item-content">
            {hasChildren && (
              <button
                className="expand-button"
                onClick={() => toggleNode(node.path)}
              >
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
            )}
            
            <button
              className="selection-button"
              onClick={() => toggleSelection(node.path, node)}
            >
              {isSelected ? (
                <Check size={16} className="check-icon" />
              ) : isPartiallySelected ? (
                <div className="partial-check">
                  <div className="partial-check-bar"></div>
                </div>
              ) : (
                <div className="empty-check"></div>
              )}
            </button>
            
            <div className="node-info">
              {node.type === 'file' ? (
                <FileText size={16} className="file-icon" />
              ) : isExpanded ? (
                <FolderOpen size={16} className="folder-icon" />
              ) : (
                <Folder size={16} className="folder-icon" />
              )}
              
              <span className="node-name">{node.name}</span>
              
              {node.fileCount > 0 && (
                <span className="file-count">
                  ({selectedCount > 0 ? `${selectedCount}/` : ''}{node.fileCount})
                </span>
              )}
            </div>
          </div>
        </div>
        
        {hasChildren && isExpanded && (
          <div className="tree-children">
            {Object.values(node.children).map(child => renderNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="hierarchical-file-selector-overlay" onKeyDown={handleKeyDown}>
      <div className="hierarchical-file-selector-modal">
        <div className="modal-header">
          <div className="header-left">
            <h2>Select Files</h2>
            <div className="selection-stats">
              {selectedFiles.length} of {selectionStats.totalFiles} selected
            </div>
          </div>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        
        <div className="modal-content">
          <div className="search-bar">
            <Search size={16} className="search-icon" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button 
                className="clear-search"
                onClick={() => setSearchQuery('')}
              >
                <X size={14} />
              </button>
            )}
          </div>
          
          <div className="file-tree">
            {loading ? (
              <div className="loading">
                <div className="loading-spinner"></div>
                <p>Loading files...</p>
              </div>
            ) : Object.keys(filteredData).length === 0 ? (
              <div className="no-files">
                <FileText size={48} className="no-files-icon" />
                <p>{searchQuery ? 'No files found matching your search' : 'No files available'}</p>
                {searchQuery && (
                  <button 
                    className="clear-search-btn"
                    onClick={() => setSearchQuery('')}
                  >
                    Clear search
                  </button>
                )}
              </div>
            ) : (
              Object.values(filteredData).map(node => renderNode(node))
            )}
          </div>
        </div>
        
        <div className="modal-footer">
          <button className="clear-button" onClick={() => onFilesSelected([])}>
            Clear All
          </button>
          <div className="action-buttons">
            <button className="cancel-button" onClick={onClose}>
              Cancel
            </button>
            <button 
              className="select-button"
              onClick={() => onClose()}
              disabled={selectedFiles.length === 0}
            >
              Select {selectedFiles.length} files
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HierarchicalFileSelector;
